import { mapValues } from "lodash";
// @ts-expect-error TS7016
import queryString from "query-string";
import { RouteComponentProps } from "react-router-dom";

import { getCookie } from "helpers/cookies";
import { ILicenseSourceParams } from "store/entities/license";

import { featureBranchURL } from "./regexp";

export type ParamValue = string | number | boolean;

export interface IKeyValue<T = ParamValue> {
  [key: string]: T | T[];
}

const queryStringOptions = { arrayFormat: "bracket", sort: false };

/**
 * Returns URL that can be safely used in:
 * - "<img src>" HTML attribute
 * - "background-image: url()" CSS attribute
 */
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function normalizeAssetUrl(url: string) {
  let targetUrl = url;
  if (!/^(http|\/\/)/.test(url)) {
    targetUrl = `//${url}`;
  }
  return targetUrl;
}

/**
 * Appends URI param to given URI address
 * @example
 *   updateQueryString(
 *     'https://www.livechatinc.com/',
 *     'foo',
 *     'bar'
 *   ); // https://www.livechatinc.com/?foo=bar
 */
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export function updateQueryString(uri: string, key: string, value: any) {
  const { url, query: parsedQuery } = queryString.parseUrl(uri, queryStringOptions);
  parsedQuery[key] = value;
  return `${url}?${stringifyQueryParams(parsedQuery)}`;
}

export function encodeURIIfNeeded(uri: string): string {
  try {
    if (decodeURI(uri) === uri) {
      return uri;
    }
    return encodeURI(decodeURIComponent(uri));
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return uri;
  }
}

/**
 * in: http://www.google.com/test/
 * out: www.google.com
 */
export function getUriHostname(uri: string): string {
  const a = document.createElement("a");
  a.href = uri;
  return a.hostname;
}

/**
 * Checks if current URL contains given path name.
 *
 * Example:
 * - in: ghost for http://my.livechatinc.com/ghost?someParam=someValue
 * - out: true
 */
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function urlContainsPathName(pathName: string) {
  return window.location.pathname.includes(pathName);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function stringifyQueryParams(params: IKeyValue) {
  return queryString.stringify(params, queryStringOptions);
}

// Taken from https://gist.github.com/penguinboy/762197#gistcomment-2120140
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function flattenObjectForUrl(object: object, separator = "."): Record<string, any> {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValidObject = (value: any): boolean => {
    if (!value) {
      return false;
    }
    const isArray = Array.isArray(value);
    const isObject = Object.prototype.toString.call(value) === "[object Object]";
    const hasKeys = !!Object.keys(value).length;
    return !isArray && isObject && hasKeys;
  };
  return Object.assign(
    {},
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ...(function _flatten(child, path = []) {
      return [].concat(
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...Object.keys(child).map((key: any): any =>
          // @ts-expect-error TS(7053)
          isValidObject(child[key])
            ? // @ts-expect-error TS(7053)
              _flatten(child[key], path.concat([key]))
            : // @ts-expect-error TS(7053)
              { [path.concat([key]).join(separator)]: child[key] }
        )
      );
    })(object)
  );
}

export function parseQueryParams(params: string): IKeyValue<string> {
  return queryString.parse(params, queryStringOptions);
}

/**
 * Returns a promise that:
 * - resolves with param value if it was found in URL, and removes the param from URL
 * - rejects if param was not found in URL
 */
export function removeParamFromUrl(
  paramKey: string,
  location: RouteComponentProps["location"],
  history: RouteComponentProps["history"]
): Promise<string | string[]> {
  return new Promise((resolve, reject) => {
    const queryParams = parseQueryParams(location.search);
    if (!queryParams[paramKey]) {
      reject();
    }

    const newQueryParams = { ...queryParams };
    const paramValue = queryParams[paramKey];
    delete newQueryParams[paramKey];
    const stringifiedParams = stringifyQueryParams(newQueryParams);
    const newParams = stringifiedParams ? `?${stringifiedParams}` : "";
    const newUrl = `${location.pathname}${newParams}${location.hash}`;
    history.replace(newUrl);
    resolve(paramValue);
  });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getAppBasename(path = window.location.pathname) {
  const matches = path.match(featureBranchURL);
  if (matches) {
    return matches[1];
  }
  return "/";
}

export function getAppRootWithOrigin(origin = window.location.origin, path = window.location.pathname): string {
  const appBasename = getAppBasename(path);
  if (appBasename !== "/") {
    return `${origin}${appBasename}`;
  }
  return origin;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getRootDomain = (hostname = window.location.hostname) => {
  const includesEnvironmentSubdomain = /labs|staging/.test(hostname);
  const sliceNumber = includesEnvironmentSubdomain ? -3 : -2;

  return hostname.split(".").slice(sliceNumber).join(".");
};

export function getFragmentParam(key: string): string | null {
  const hashString = new URL(window.location.href).hash;
  if (!hashString) {
    return null;
  }

  return new URLSearchParams(hashString.substring(1)).get(key);
}

export const utmParams = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export function getLicenseSourceParams(): ILicenseSourceParams {
  // getCookie can return number as a result of JSON.parse
  return mapValues(
    {
      utmSource: getCookie("utm_source"),
      utmMedium: getCookie("utm_medium"),
      utmCampaign: getCookie("utm_campaign"),
      utmContent: getCookie("utm_content"),
      utmTerm: getCookie("utm_term"),
      referrer: getCookie("referrer"),
      landingPage: getCookie("landing_page"),
      sourceURL: getCookie("source_url"),
      sourceID: getCookie("source_id"),
      sourceType: getCookie("source_type"),
    },
    value => {
      if (!value || typeof value === "string") {
        return value;
      }
      if (typeof value === "number") {
        return value.toString();
      }
      return null; // in case of parsed object
    }
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function downloadBlob(blobUrl: string, filename: string) {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  // @ts-expect-error TS(2531): Object is possibly 'null'.
  link.parentNode.removeChild(link);
}
